import { Flex, Text, Tooltip } from "@fluentui/react-northstar";
import { DialogDimension, DialogInfo } from "@microsoft/teams-js";
import {
  SurveyCloseCommand,
  getSurveyTypeDisplay,
  scheduleDeleteCommand,
  scheduleDisableCommand,
  scheduleEnableCommand,
} from "@teams/cards";
import { TeamsSurveyState } from "@teams/types";
import { ScheduleDTO, UserDTO } from "@teams/types/api";
import React, { useState } from "react";

import { TemplateSaveDialog } from "../../../../survey/pages/SurveyAuthoring/Templates/TemplateSaveDialog";
import CoreLogo from "../../../assets/polly-logo-no-text.svg";
import { FetchError, isError } from "../../../services/ChannelApi";
import { useTeamsContext } from "../../../state/TeamsContextProvider";
import { clientEventTracker } from "../../../utils/clientTelemetry";
import { downloadSeriesCsv } from "../../../utils/pollyResultsExport";
import { startTaskGenericConfirmation } from "../../../utils/startTaskGenericConfirmation";
import { startTask } from "../../../utils/startTaskWrapper";
import { getAppId, getPageContext } from "../../../utils/teamsHelpers";
import { AutolinkListDialog } from "../../autolink/AutolinkListDialog";
import { OverflowMenu, OverflowMenuItem } from "../../Menus/OverflowMenu/OverflowMenu";
import { PollyDialog } from "../../PollyDialog";
import { PollyIcon } from "../../PollyIcon";
import { ResultsExportCompletionDialog } from "../../ResultsExportCompletionDialog";
import { EditScheduleDialog } from "./EditScheduleDialog";
import { ShareScheduleDialog } from "./ShareScheduleDialog";

type SeriesMenuProps = {
  schedule: ScheduleDTO;
  user: UserDTO;
  onChanged: () => void;
  showEditOption?: boolean;
};

export const SeriesMenu: React.FC<SeriesMenuProps> = ({
  schedule,
  onChanged,
  user,
  showEditOption,
}) => {
  const { teamsContext } = useTeamsContext();
  const [isUsersOpen, setIsUsersOpen] = useState<boolean>(false);
  const [isAutolinkOpen, setIsAutolinkOpen] = useState<boolean>(false);
  const [isEditSelectionOpen, setIsEditSelectionOpen] = useState<boolean>(false);
  const [saveTemplateDialogOpen, setSaveTemplateDialogOpen] = useState<boolean>(false);
  const [csvExportSuccess, setCsvExportSuccess] = useState<boolean>();
  const [csvExportResult, setCsvExportResult] = useState<string>();

  const menuItems: OverflowMenuItem[] = [];

  if (!schedule.template) {
    return null;
  }

  const hasRecurringFeature =
    schedule.applicationType === "agile"
      ? user.featureFlags.agileStandup
      : user.featureFlags.surveyRecurring;

  if (user.featureFlags.surveyExports) {
    menuItems.push({
      key: "export",
      showInOverflow: false,
      icon: (
        <Tooltip content="Export Results">
          <span>
            <PollyIcon icon="download-icon" inheritColor />
          </span>
        </Tooltip>
      ),
      onClick: async () => {
        const result = await downloadSeriesCsv(schedule, teamsContext);
        if (isError(result)) {
          console.error(result);
          setCsvExportResult((result as FetchError).body?.message as string);
          setCsvExportSuccess(false);
        } else {
          setCsvExportResult(result);
          setCsvExportSuccess(true);
        }
      },
    });
  }

  if (schedule.role === "own") {
    const overflowItems: OverflowMenuItem[] = [];
    if (showEditOption !== false) {
      overflowItems.push({
        key: "edit",
        showInOverflow: true,
        disabled: !hasRecurringFeature,
        icon: <PollyIcon icon="edit-icon" inheritColor />,
        content: "Edit",
        onClick: () => {
          if (
            schedule.applicationType !== "agile" &&
            user.featureFlags.allowPollyEdit &&
            schedule.lastSurvey &&
            schedule.lastSurvey?.state === "open"
          ) {
            setIsEditSelectionOpen(true);
          } else {
            openAuthoring(schedule, false, onChanged);
          }
        },
      });
    }

    if (schedule.template.type === "agile-standup") {
      overflowItems.push({
        key: "autolink",
        showInOverflow: true,
        icon: <PollyIcon icon="integration-icon" inheritColor />,
        content: "Integrations",
        onClick: () => {
          setIsAutolinkOpen(true);
          clientEventTracker.track("integrations clicked", user, {});
        },
      });
    }

    overflowItems.push(
      ...[
        {
          key: "save",
          showInOverflow: true,
          icon: <PollyIcon icon="my-templates-icon" inheritColor />,
          content: "Save as Template",
          onClick: () => {
            setSaveTemplateDialogOpen(true);
          },
        },
        {
          key: "owners",
          showInOverflow: true,
          icon: <PollyIcon icon="share-icon" inheritColor />,
          content: "Share",
          disabled: !hasRecurringFeature,
          onClick: () => {
            setIsUsersOpen(true);
          },
        },
        {
          key: "disable",
          showInOverflow: true,
          icon:
            schedule.state === "active" ? (
              <PollyIcon icon="disable-icon" inheritColor />
            ) : (
              <PollyIcon icon="enable-icon" inheritColor />
            ),
          content: schedule.state === "disabled" ? "Enable" : "Disable",
          disabled: schedule.state !== "active" && !hasRecurringFeature,
          onClick: () => {
            const label =
              schedule.state === "disabled"
                ? "Are you sure you want to enable the series?"
                : "Are you sure you want to disable the series?";
            const buttonTitle = schedule.state === "disabled" ? "Enable series" : "Disable series";
            const command =
              schedule.state === "disabled"
                ? scheduleEnableCommand(schedule.id)
                : scheduleDisableCommand(schedule.id);

            startTaskGenericConfirmation(
              label,
              " ",
              buttonTitle,
              schedule.applicationType,
              onChanged,
              buttonTitle,
              "Cancel",
              command,
              "ALERT_WARN",
              true
            );
          },
        },
        {
          key: "delete",
          showInOverflow: true,
          icon: <PollyIcon icon="trash-icon" inheritColor />,
          content: "Delete",
          onClick: () => {
            startTaskGenericConfirmation(
              "Delete series",
              "All messages & data related to this series will be deleted.",
              "Delete",
              schedule.applicationType,
              onChanged,
              "Delete series",
              "Cancel",
              scheduleDeleteCommand({ scheduleId: schedule.id }),
              "ALERT_DANGER",
              true
            );
          },
        },
      ]
    );

    if (
      schedule.lastSurvey?.id &&
      (["pending", "open"] as TeamsSurveyState[]).includes(schedule.lastSurvey.state)
    )
      menuItems.push({
        key: "close",
        showInOverflow: false,
        icon: (
          <Tooltip content="Close">
            <span>
              <PollyIcon icon="close-poll-icon" inheritColor />
            </span>
          </Tooltip>
        ),
        onClick: () => {
          const label = getSurveyTypeDisplay(schedule.template.type);
          startTaskGenericConfirmation(
            `Close ${label} now?`,
            "Audience will no longer be able to respond to it unless you reopen it.",
            "Close",
            schedule.applicationType,
            onChanged,
            `Close ${label}`,
            "Cancel",
            SurveyCloseCommand(schedule.lastSurvey?.id as string),
            "ALERT_WARN",
            true
          );
        },
      });
    menuItems.push(...overflowItems);
  }

  if (!menuItems.length) {
    return null;
  }

  return (
    <>
      <OverflowMenu items={menuItems} maxExposedItemsCount={2} />

      {isEditSelectionOpen && (
        <PollyDialog
          open
          onCancel={() => {
            setIsEditSelectionOpen(false);
          }}
          maxWidth="410px"
          className="edit-dialog"
          header={
            <Flex className="edit-dialog-header">
              <img src={CoreLogo} height={40} alt="Polly" />
              <Flex column className="edit-dialog-header__heading">
                <Text weight="bold" size="medium">
                  Polly
                </Text>
                <Text weight="regular" size="medium">
                  Edit recurring polly
                </Text>
              </Flex>
            </Flex>
          }
        >
          <EditScheduleDialog
            setIsEditSelectionOpen={setIsEditSelectionOpen}
            openAuthoring={(limitedEdit: boolean) => {
              openAuthoring(schedule, limitedEdit, onChanged);
            }}
          />
        </PollyDialog>
      )}

      {isUsersOpen && (
        <ShareScheduleDialog schedule={schedule} onChanged={onChanged} setIsOpen={setIsUsersOpen} />
      )}

      {isAutolinkOpen && (
        <AutolinkListDialog
          reference={schedule}
          referenceType="schedule"
          setIsOpen={setIsAutolinkOpen}
          onChanged={onChanged}
        />
      )}

      <TemplateSaveDialog
        open={saveTemplateDialogOpen}
        header="Save to template"
        closeOnOutsideClick
        showViewTemplateButton
        onClose={() => setSaveTemplateDialogOpen(false)}
        onSave={() => schedule.template}
      />

      <ResultsExportCompletionDialog
        onCancel={() => {
          setCsvExportResult(undefined);
          setCsvExportSuccess(undefined);
        }}
        exportResult={csvExportResult}
        exportSuccess={csvExportSuccess}
      />
    </>
  );
};

function openAuthoring(schedule: ScheduleDTO, limitedEdit: boolean, onChanged: () => void) {
  const taskInfo: DialogInfo = {
    url: `${window.location.origin}${getPageContext()}/content/polly/series/${
      schedule.id
    }/edit?limitedEdit=${limitedEdit}`,
    height: DialogDimension.Large,
    width: 700,
    title: "Edit",
    completionBotId: getAppId(),
  };
  startTask(taskInfo, () => {
    onChanged();
  });
}
